/* Fuseau d'affichage de la surface d'audit. Épinglé, jamais celui du
   navigateur : deux auditeurs qui regardent la même conversation depuis deux
   fuseaux doivent lire le même jour, sinon l'écran ne peut pas servir de
   preuve. Toute date de cette surface passe par ce module. */
export const AUDIT_TIME_ZONE = 'Europe/Paris';
export const AUDIT_TIME_ZONE_LABEL = 'heure de Paris';

const NO_DATE = '—';

const timeFmt = new Intl.DateTimeFormat('fr-FR', {
  timeZone: AUDIT_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const dayFmt = new Intl.DateTimeFormat('fr-FR', {
  timeZone: AUDIT_TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', {
  timeZone: AUDIT_TIME_ZONE,
  dateStyle: 'medium',
  timeStyle: 'short',
});

const absoluteFmt = new Intl.DateTimeFormat('fr-FR', {
  timeZone: AUDIT_TIME_ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: AUDIT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const zonedPartsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: AUDIT_TIME_ZONE,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/* Instant UTC dont l'heure murale en Europe/Paris est y-m-d hh:mm:ss. Devine
   l'instant en traitant la date comme si elle était déjà UTC, lit l'heure que
   ça donnerait réellement à Paris, puis corrige de l'écart — l'algorithme
   standard pour inverser un formatage de fuseau. Minuit et 23:59:59 tombent
   toujours hors de la fenêtre de bascule DST (qui a lieu vers 1h-3h locale),
   donc jamais dans l'heure ambiguë ou l'heure inexistante. */
function parisWallTimeToUtc(y: number, m: number, d: number, hh: number, mm: number, ss: number): Date {
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss);
  const parts = zonedPartsFmt.formatToParts(new Date(guess));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const seenAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return new Date(guess - (seenAsUtc - guess));
}

function parseDateOnly(dateOnly: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/* Bornes d'un filtre de période saisi comme jour civil (input[type=date],
   sans heure ni fuseau) : le jour doit s'entendre au sens de Paris, jamais au
   sens du fuseau du lecteur, sinon deux auditeurs dans deux fuseaux qui tapent
   "du 02/09 au 02/09" n'interrogent pas la même tranche. */
export function startOfAuditDayUtc(dateOnly: string): Date | null {
  const parsed = parseDateOnly(dateOnly);
  return parsed === null ? null : parisWallTimeToUtc(parsed[0], parsed[1], parsed[2], 0, 0, 0);
}

export function endOfAuditDayUtc(dateOnly: string): Date | null {
  const parsed = parseDateOnly(dateOnly);
  return parsed === null ? null : parisWallTimeToUtc(parsed[0], parsed[1], parsed[2], 23, 59, 59);
}

export function auditDayKey(iso: string | null | undefined): string | null {
  const d = parse(iso);
  return d === null ? null : dayKeyFmt.format(d);
}

export function fmtAuditTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d === null ? NO_DATE : timeFmt.format(d);
}

export function fmtAuditDay(iso: string | null | undefined): string {
  const d = parse(iso);
  if (d === null) return NO_DATE;
  const label = dayFmt.format(d);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function fmtAuditDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d === null ? NO_DATE : dateTimeFmt.format(d);
}

export function fmtAuditAbsolute(iso: string | null | undefined): string {
  const d = parse(iso);
  return d === null ? NO_DATE : `${absoluteFmt.format(d)} (${AUDIT_TIME_ZONE_LABEL})`;
}
